/**
 * Website Source Form
 * Register one or more URLs as a TraceOS website source for fetching, parsing, and RAG indexing.
 */

import React, { useState } from 'react';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { registerWebsiteSource } from '../../api/sources';
import { Globe, Plus, Trash2, AlertCircle, CheckCircle, Loader } from 'lucide-react';

interface UrlEntry {
  url: string;
  title: string;
  category: string;
}

const CATEGORY_OPTIONS = [
  'government_report',
  'research_paper',
  'news_article',
  'reference',
  'regulation',
  'technical_standard',
  'other',
];

const EXTRACTION_MODES = [
  { value: 'readability', label: 'Readability (recommended)', description: 'Extracts main article text, strips nav/ads' },
  { value: 'text_only', label: 'Text only', description: 'Raw text extraction, no HTML parsing' },
  { value: 'full_html', label: 'Full HTML', description: 'Preserve full HTML structure' },
];

function isValidUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

interface WebsiteFormProps {
  projectId: string;
  onSuccess: () => void;
  onClose: () => void;
}

export function WebsiteForm({ projectId, onSuccess, onClose }: WebsiteFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [extractionMode, setExtractionMode] = useState<'readability' | 'full_html' | 'text_only'>('readability');
  const [refreshFrequency, setRefreshFrequency] = useState<'manual' | 'daily' | 'weekly'>('manual');
  const [urlInput, setUrlInput] = useState('');
  const [urls, setUrls] = useState<UrlEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const addUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    if (!isValidUrl(trimmed)) {
      setError(`Invalid URL: "${trimmed}". Must start with http:// or https://`);
      return;
    }
    if (urls.some((u) => u.url === trimmed)) {
      setError('This URL is already in the list.');
      return;
    }
    setError(null);
    setUrls((prev) => [...prev, { url: trimmed, title: '', category: 'reference' }]);
    setUrlInput('');
  };

  const handleUrlInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addUrl();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Auto-split multi-line paste into multiple URLs
    const pasted = e.clipboardData.getData('text');
    const lines = pasted.split(/[\n\r,\s]+/).map((l) => l.trim()).filter(Boolean);
    if (lines.length > 1) {
      e.preventDefault();
      setError(null);
      const valid: UrlEntry[] = [];
      const invalid: string[] = [];
      for (const line of lines) {
        if (isValidUrl(line) && !urls.some((u) => u.url === line) && !valid.some((u) => u.url === line)) {
          valid.push({ url: line, title: '', category: 'reference' });
        } else if (!isValidUrl(line)) {
          invalid.push(line);
        }
      }
      if (invalid.length > 0) {
        setError(`Skipped ${invalid.length} invalid URL(s): ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? ' …' : ''}`);
      }
      setUrls((prev) => [...prev, ...valid]);
    }
  };

  const removeUrl = (index: number) => {
    setUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const updateUrl = (index: number, field: keyof UrlEntry, value: string) => {
    setUrls((prev) => prev.map((u, i) => (i === index ? { ...u, [field]: value } : u)));
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Source name is required.'); return; }
    if (urls.length === 0) { setError('Add at least one URL.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await registerWebsiteSource({
        project_id: projectId,
        name: name.trim(),
        description: description.trim() || undefined,
        urls: urls.map((u) => ({
          url: u.url,
          title: u.title.trim() || undefined,
          category: u.category || 'reference',
        })),
        extraction_mode: extractionMode,
        max_content_length: 200_000,
        refresh_frequency: refreshFrequency,
      });
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1200);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Registration failed. Check API logs.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center py-8 gap-3 text-green-600">
        <CheckCircle className="h-10 w-10" />
        <p className="font-semibold">Website source registered!</p>
        <p className="text-sm text-gray-500">Sync will begin automatically.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Name + Description */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Source name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="e.g. MARPAC Reference Web Sources"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <Input
            placeholder="Optional — describe what these URLs cover"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>

      {/* Extraction mode + refresh frequency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Extraction mode
          </label>
          <select
            value={extractionMode}
            onChange={(e) => setExtractionMode(e.target.value as typeof extractionMode)}
            className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-slate-700 text-theme-text text-sm focus:ring-2 focus:ring-teal-500"
          >
            {EXTRACTION_MODES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {EXTRACTION_MODES.find((m) => m.value === extractionMode)?.description}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Refresh frequency
          </label>
          <select
            value={refreshFrequency}
            onChange={(e) => setRefreshFrequency(e.target.value as typeof refreshFrequency)}
            className="w-full px-3 py-2 border border-theme-border rounded-lg bg-white dark:bg-slate-700 text-theme-text text-sm focus:ring-2 focus:ring-teal-500"
          >
            <option value="manual">Manual (sync on demand)</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
        </div>
      </div>

      {/* URL input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          URLs <span className="text-red-500">*</span>
          <span className="ml-2 text-xs font-normal text-gray-500">
            Press Enter or paste multiple lines to add. PDFs and HTML pages both supported.
          </span>
        </label>
        <div className="flex gap-2">
          <Input
            placeholder="https://example.com/document"
            value={urlInput}
            onChange={(e) => { setUrlInput(e.target.value); setError(null); }}
            onKeyDown={handleUrlInputKeyDown}
            onPaste={handlePaste}
            className="flex-1 font-mono text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={addUrl}
            disabled={!urlInput.trim()}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>
      </div>

      {/* URL list */}
      {urls.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {urls.length} URL{urls.length !== 1 ? 's' : ''} registered
            </span>
            {urls.length > 1 && (
              <button
                onClick={() => setUrls([])}
                className="text-xs text-red-500 hover:text-red-700 hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
            {urls.map((entry, i) => (
              <div
                key={i}
                className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/50"
              >
                <Globe className="h-4 w-4 text-teal-500 flex-shrink-0" />
                <span className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate flex-1" title={entry.url}>
                  {entry.url}
                </span>
                <input
                  type="text"
                  placeholder="Title (optional)"
                  value={entry.title}
                  onChange={(e) => updateUrl(i, 'title', e.target.value)}
                  className="w-32 px-2 py-1 text-xs border border-theme-border rounded bg-white dark:bg-slate-700 text-theme-text focus:outline-none focus:ring-1 focus:ring-teal-500 flex-shrink-0"
                />
                <select
                  value={entry.category}
                  onChange={(e) => updateUrl(i, 'category', e.target.value)}
                  className="w-36 px-1.5 py-1 text-xs border border-theme-border rounded bg-white dark:bg-slate-700 text-theme-text focus:outline-none focus:ring-1 focus:ring-teal-500 flex-shrink-0"
                >
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                  ))}
                </select>
                <button
                  onClick={() => removeUrl(i)}
                  className="text-gray-400 hover:text-red-500 flex-shrink-0"
                  title="Remove URL"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2 border-t dark:border-slate-700">
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || urls.length === 0 || !name.trim()}
          className="bg-teal-600 hover:bg-teal-700 text-white"
        >
          {submitting ? (
            <>
              <Loader className="h-4 w-4 animate-spin" />
              Registering…
            </>
          ) : (
            <>
              <Globe className="h-4 w-4" />
              Register {urls.length > 0 ? `${urls.length} URL${urls.length !== 1 ? 's' : ''}` : 'Source'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
